// Lote UX Leyro parity — barra de categorías sticky inspirada na
// CategoryBar de Leyro (apps/web/src/components/layout/CategoryBar.jsx
// en pinifiux/Leiro). Chips horizontais scrolleables + botón "Filtros"
// cun popover que contén o selector de provincia.
//
// Comunícase con MarketplacePage **exclusivamente vía URL search params**
// (?destination=...&province=...) — non hai prop drilling nin estado
// compartido. Ambos compoñentes len/escriben `useSearchParams` e reaccionan
// ao cambio. Mantén a refactor cero-funcional: a lóxica de fetching e
// filtrado segue íntegra en MarketplacePage.
//
// Aparece soamente na ruta exacta `/marketplace` (a listing). Detalle,
// checkout, profile, etc. non a renderizan (control en Layout.tsx).

import {
  Beef,
  ChevronLeft,
  ChevronRight,
  Filter,
  Gavel,
  Heart,
  LayoutGrid,
  MapPin,
  Milk,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

type DestFilter = 'ALL' | 'BEEF' | 'DAIRY' | 'BREEDING' | 'AUCTION';
type ProvinceFilter = 'ALL' | 'A_CORUNA' | 'LUGO' | 'OURENSE' | 'PONTEVEDRA';

interface ChipDef {
  id: DestFilter;
  labelKey: string;
  icon: LucideIcon;
}

const CHIPS: ChipDef[] = [
  { id: 'ALL', labelKey: 'marketplace.types.ALL', icon: LayoutGrid },
  { id: 'BEEF', labelKey: 'marketplace.types.BEEF', icon: Beef },
  { id: 'DAIRY', labelKey: 'marketplace.types.DAIRY', icon: Milk },
  { id: 'BREEDING', labelKey: 'marketplace.types.BREEDING', icon: Heart },
  { id: 'AUCTION', labelKey: 'marketplace.types.AUCTION', icon: Gavel },
];

const PROVINCES: { id: ProvinceFilter; labelKey: string }[] = [
  { id: 'ALL', labelKey: 'marketplace.provinces.ALL' },
  { id: 'A_CORUNA', labelKey: 'marketplace.provinces.A_CORUNA' },
  { id: 'LUGO', labelKey: 'marketplace.provinces.LUGO' },
  { id: 'OURENSE', labelKey: 'marketplace.provinces.OURENSE' },
  { id: 'PONTEVEDRA', labelKey: 'marketplace.provinces.PONTEVEDRA' },
];

export default function CategoryBar(): JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const activeDest = (params.get('destination') as DestFilter | null) ?? 'ALL';
  const activeProvince = (params.get('province') as ProvinceFilter | null) ?? 'ALL';
  const filterCount = activeProvince === 'ALL' ? 0 : 1;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  function scrollBy(direction: number): void {
    scrollRef.current?.scrollBy({ left: direction * 240, behavior: 'smooth' });
  }

  function setSearchParam(key: 'destination' | 'province', value: string): void {
    const next = new URLSearchParams(params);
    if (value === 'ALL') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  // Popover de filtros (provincia). Pechase ao clicar fora ou con ESC.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return;
    function onPointerDown(ev: MouseEvent | TouchEvent): void {
      const root = filtersRef.current;
      if (root && !root.contains(ev.target as Node)) setFiltersOpen(false);
    }
    function onKey(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  return (
    <div className="sticky top-[72px] z-30 bg-white border-b border-border">
      <div className="max-w-[1760px] mx-auto px-6 flex items-center">
        {canScrollLeft && (
          <button
            type="button"
            className="flex-shrink-0 mr-2 rounded-full p-1.5 border border-border bg-white hover:bg-subtle shadow-card transition-colors"
            onClick={() => scrollBy(-1)}
            aria-label={t('marketplace.categoryBar.scrollLeft')}
          >
            <ChevronLeft size={16} className="text-dark" />
          </button>
        )}

        <div ref={scrollRef} className="flex gap-6 overflow-x-auto hide-scrollbar flex-1 min-w-0">
          {CHIPS.map((chip) => {
            const Icon = chip.icon;
            const isActive = activeDest === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                className={`flex flex-col items-center gap-1 cursor-pointer min-w-fit pb-3 pt-4 border-b-2 transition-colors ${
                  isActive
                    ? 'border-dark text-dark'
                    : 'border-transparent text-muted hover:text-dark hover:border-border'
                }`}
                onClick={() => setSearchParam('destination', isActive ? 'ALL' : chip.id)}
                aria-pressed={isActive}
                aria-label={t(chip.labelKey)}
              >
                <Icon size={24} aria-hidden="true" />
                <span className="text-xs font-medium whitespace-nowrap">{t(chip.labelKey)}</span>
              </button>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="flex-shrink-0 ml-2 rounded-full p-1.5 border border-border bg-white hover:bg-subtle shadow-card transition-colors"
            onClick={() => scrollBy(1)}
            aria-label={t('marketplace.categoryBar.scrollRight')}
          >
            <ChevronRight size={16} className="text-dark" />
          </button>
        )}

        {/* Botón "Filtros" + popover con provincia */}
        <div ref={filtersRef} className="relative ml-4 shrink-0">
          <button
            type="button"
            className="flex items-center gap-2 border border-border rounded-xl px-4 py-2.5 text-sm font-semibold text-dark hover:bg-subtle relative transition-colors"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={
              filterCount > 0
                ? t('marketplace.categoryBar.filtersWithCount', { count: filterCount })
                : t('marketplace.categoryBar.filters')
            }
          >
            <Filter size={16} aria-hidden="true" />
            <span>{t('marketplace.categoryBar.filters')}</span>
            {filterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-rauxa text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                {filterCount}
              </span>
            )}
          </button>

          {filtersOpen && (
            <div
              role="dialog"
              aria-label={t('marketplace.categoryBar.filtersDialog')}
              className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-card-hover border border-border p-4 z-40 animate-fade-in"
            >
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted mb-3">
                <MapPin size={14} className="text-rauxa" aria-hidden="true" />
                {t('marketplace.provinceLabel')}
              </div>
              <div className="flex flex-col gap-1.5">
                {PROVINCES.map((p) => {
                  const isActive = activeProvince === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSearchParam('province', p.id);
                        setFiltersOpen(false);
                      }}
                      className={`text-left text-sm rounded-xl px-3 py-2 transition-colors ${
                        isActive ? 'bg-subtle font-semibold text-dark' : 'text-dark hover:bg-subtle'
                      }`}
                    >
                      {t(p.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
